import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Close as CloseIcon } from "@mui/icons-material";
import PropTypes from "prop-types";

const FileViewerDialog = ({ fileUrl, fileType, onClose, t }) => {
  const theme = useTheme();
  const isImage = fileType === "image";

  return (
    <Dialog
      open={!!fileUrl}
      onClose={onClose}
      maxWidth={isImage ? false : "md"}
      fullWidth={!isImage}
      fullScreen={isImage}
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: isImage ? 0 : 3,
          bgcolor: isImage ? "rgba(0, 0, 0, 0.9)" : "background.paper",
          boxShadow: 8,
        },
      }}
      aria-labelledby="file-viewer-dialog-title"
    >
      {!isImage && (
        <DialogTitle
          id="file-viewer-dialog-title"
          sx={{
            bgcolor: "background.paper",
            borderBottom: "1px solid",
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: { xs: 2, sm: 2.5 },
            px: { xs: 2, sm: 3 },
          }}
        >
          <Typography sx={{ fontWeight: 600, fontSize: "1.25rem" }}>
            {t("observationDetails.attachmentPreviewTitle")}
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{
              bgcolor: "action.hover",
              "&:hover": {
                bgcolor: "action.selected",
                transform: "scale(1.05)",
              },
              transition: "all 0.2s ease-in-out",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
      )}

      {/* Full-screen image close button */}
      {isImage && (
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            zIndex: 1000,
            bgcolor: "rgba(0, 0, 0, 0.7)",
            color: "white",
            width: 48,
            height: 48,
            "&:hover": {
              bgcolor: "rgba(0, 0, 0, 0.9)",
              transform: "scale(1.1)",
            },
            transition: "all 0.2s ease-in-out",
          }}
          aria-label={t(
            "observationDetails.closeFullScreenImage",
            "Close full screen image"
          )}
        >
          <CloseIcon />
        </IconButton>
      )}
      <DialogContent
        dividers={!isImage}
        sx={{
          height: isImage ? "100vh" : { xs: "70vh", sm: "80vh" },
          p: isImage ? 0 : 0,
          bgcolor: isImage ? "transparent" : "background.default",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {fileType === "pdf" && (
          <embed
            src={fileUrl}
            type="application/pdf"
            width="100%"
            height="100%"
            title={t("observationDetails.pdfPreviewTitle")}
            aria-label={t("observationDetails.pdfPreviewAria")}
          />
        )}
        {fileType === "office" && (
          <iframe
            src={`https://docs.google.com/viewer?url=${encodeURIComponent(fileUrl)}&embedded=true`}
            width="100%"
            height="100%"
            style={{ border: 0 }}
            title={t("observationDetails.officePreviewTitle")}
          />
        )}
        {fileType === "image" && (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "relative",
            }}
            onClick={onClose} // Click anywhere to close in full-screen mode
          >
            <img
              src={fileUrl}
              alt={t("observationDetails.attachmentAlt")}
              style={{
                maxWidth: isImage ? "95vw" : "100%",
                maxHeight: isImage ? "95vh" : "100%",
                objectFit: "contain",
                borderRadius: isImage ? 0 : theme.shape.borderRadius,
                cursor: isImage ? "pointer" : "default",
              }}
              onClick={(e) => {
                if (isImage) {
                  e.stopPropagation();
                  // Double-click or specific action to close
                }
              }}
            />
          </Box>
        )}
        {fileType === "other" && (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              p: 2,
              bgcolor: theme.palette.checklist.surface.primary,
            }}
          >
            <Typography variant="body1" gutterBottom>
              {t("observationDetails.cannotPreview")}
            </Typography>
            <Button
              variant="outlined"
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ borderRadius: 2 }}
            >
              {t("observationDetails.downloadFileButton")}
            </Button>
          </Box>
        )}
      </DialogContent>
      {!isImage && (
        <DialogActions
          sx={{
            p: 2,
            bgcolor: theme.palette.checklist.surface.subtle,
            borderTop: 1,
            borderColor: theme.palette.checklist.border.subtle,
          }}
        >
          <Button
            onClick={onClose}
            color="primary"
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            {t("observationDetails.closeButton")}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

FileViewerDialog.propTypes = {
  fileUrl: PropTypes.string,
  fileType: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

export default FileViewerDialog;
